import { useDataProvider } from 'react-admin';
import { isNativeLineItemId } from 'src/client/utility/pio-native-lineitem-fk-reads';

export const useTransformWithCampaignId = () => {
  const dataProvider = useDataProvider();

  const transform = async (data: any) => {
    try {
      if (!data?.placementsLineItemId) {
        throw new Error('Line item ID is required to create a lead gen creative');
      }

      // MD-5145: native line items are keyed by their native UUID. Leave
      // placementsCampaignId undefined; the placements_campaign_id column is
      // nullable for native creatives and the native lineItemId FK is resolved
      // on write instead.
      if (isNativeLineItemId(data.placementsLineItemId)) {
        return data;
      }

      // Imported line items: the Placements.IO API shut off at contract end, so
      // the legacy campaign id must come from native data — the line item row
      // (keyed by externalPioId) leads to its campaign, whose externalPioId is
      // the legacy Placements campaign id. Keeping placements_campaign_id keyed
      // in the legacy id-space preserves competitive-exclusion matching against
      // imported campaign labels.
      // Explicit equalTo: ra-postgraphile maps a plain string filter value to
      // the substring `includes` operator, which could match the wrong row.
      const { data: lineItems } = await dataProvider.getList('lineItems', {
        filter: {
          externalPioId: { operator: 'equalTo', value: String(data.placementsLineItemId) },
          includeArchived: true,
        },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      });
      const lineItem = lineItems?.[0];
      if (!lineItem) {
        throw new Error('Error fetching campaign ID');
      }

      const { data: campaign } = await dataProvider.getOne('campaigns', { id: lineItem.campaignId });
      const legacyCampaignId = campaign?.externalPioId;

      return legacyCampaignId
        ? {
            ...data,
            placementsCampaignId: legacyCampaignId,
          }
        : data;
    } catch (error) {
      console.error('Error fetching campaign ID:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error fetching campaign ID');
    }
  };

  return transform;
};
